/**
 * Elite Fade Designs - Customer Auth Service
 *
 * Wraps amazon-cognito-identity-js for the public customer pool.
 * Distinct from auth.service.js (admin pool) so admin and customer
 * sessions never collide.
 *
 * Customer tokens live in localStorage so the session survives
 * tab close (different from admin which uses sessionStorage).
 *
 * Public surface:
 *   signUp({ email, password, name, phone })       -> { needsConfirmation, email }
 *   confirmSignUp(email, code)                     -> void
 *   resendConfirmation(email)                      -> void
 *   signIn(email, password)                        -> { tokens, user }
 *   signOut()                                      -> void
 *   getSession()                                   -> { tokens, user } | null
 *   isAuthenticated()                              -> Promise<boolean>
 *   forgotPassword(email)                          -> void
 *   confirmForgotPassword(email, code, password)   -> void
 *
 *   --- preferences ---
 *   getPreferredBarber()                           -> string | null
 *   setPreferredBarber(staffId)                    -> Promise<void>
 *   clearPreferredBarber()                         -> Promise<void>
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { config } from '../config/index.js';

const pool = new CognitoUserPool({
  UserPoolId: config.cognito.customer.userPoolId,
  ClientId:   config.cognito.customer.clientId,
});

const SESSION_KEY = 'efd-customer-session';

// ---------------------------------------------------------------------------
// Sign up
// ---------------------------------------------------------------------------

export function signUp({ email, password, name, phone }) {
  const attrs = [
    new CognitoUserAttribute({ Name: 'email', Value: email.trim().toLowerCase() }),
    new CognitoUserAttribute({ Name: 'name',  Value: name.trim() }),
  ];
  if (phone && phone.trim()) {
    attrs.push(new CognitoUserAttribute({
      Name: 'phone_number',
      Value: normalizePhone(phone),
    }));
  }

  return new Promise((resolve, reject) => {
    pool.signUp(email.trim().toLowerCase(), password, attrs, null, (err, result) => {
      if (err) return reject(translateError(err));
      resolve({
        email: email.trim().toLowerCase(),
        needsConfirmation: !result?.userConfirmed,
      });
    });
  });
}

export function confirmSignUp(email, code) {
  const user = new CognitoUser({
    Username: email.trim().toLowerCase(),
    Pool: pool,
  });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code.trim(), true, (err) => {
      if (err) return reject(translateError(err));
      resolve();
    });
  });
}

export function resendConfirmation(email) {
  const user = new CognitoUser({
    Username: email.trim().toLowerCase(),
    Pool: pool,
  });
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err) => {
      if (err) return reject(translateError(err));
      resolve();
    });
  });
}

// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------

export function signIn(email, password) {
  const cleanEmail = email.trim().toLowerCase();
  const user = new CognitoUser({ Username: cleanEmail, Pool: pool });
  const auth = new AuthenticationDetails({ Username: cleanEmail, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => {
        const idPayload = session.getIdToken().payload;
        const payload = {
          accessToken:  session.getAccessToken().getJwtToken(),
          idToken:      session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          email:        idPayload.email,
          name:         idPayload.name,
          sub:          idPayload.sub,
          expiresAt:    idPayload.exp * 1000,
          // Custom attribute lives in the JWT once present.
          // May be undefined for accounts that haven't set one yet.
          preferredBarber: idPayload['custom:preferred_barber'] || null,
        };
        persist(payload);
        resolve(payload);
      },
      onFailure: (err) => reject(translateError(err)),
      newPasswordRequired: () => {
        // We never force temporary passwords on customers.
        reject({ code: 'NEW_PASSWORD_REQUIRED', message: 'Password reset required.' });
      },
    });
  });
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.idToken || !session?.expiresAt) return null;
    if (Date.now() >= session.expiresAt) {
      // Token expired - try to refresh transparently
      return refreshIfPossible();
    }
    return session;
  } catch (_) {
    return null;
  }
}

export async function isAuthenticated() {
  const s = getSession();
  if (!s) return false;
  if (s instanceof Promise) {
    const refreshed = await s;
    return !!refreshed;
  }
  return true;
}

function refreshIfPossible() {
  // Returns a promise; callers that don't await get null treated as "not yet"
  return new Promise((resolve) => {
    const cognitoUser = pool.getCurrentUser();
    if (!cognitoUser) {
      clearSession();
      return resolve(null);
    }
    cognitoUser.getSession((err, session) => {
      if (err || !session?.isValid()) {
        clearSession();
        return resolve(null);
      }
      const idPayload = session.getIdToken().payload;
      const payload = {
        accessToken:  session.getAccessToken().getJwtToken(),
        idToken:      session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        email:        idPayload.email,
        name:         idPayload.name,
        sub:          idPayload.sub,
        expiresAt:    idPayload.exp * 1000,
        preferredBarber: idPayload['custom:preferred_barber'] || null,
      };
      persist(payload);
      resolve(payload);
    });
  });
}

export function signOut() {
  const cognitoUser = pool.getCurrentUser();
  if (cognitoUser) cognitoUser.signOut();
  clearSession();
}

// ---------------------------------------------------------------------------
// Forgot password
// ---------------------------------------------------------------------------

export function forgotPassword(email) {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(translateError(err)),
    });
  });
}

export function confirmForgotPassword(email, code, newPassword) {
  const user = new CognitoUser({ Username: email.trim().toLowerCase(), Pool: pool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code.trim(), newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(translateError(err)),
    });
  });
}

// ---------------------------------------------------------------------------
// Preferences (custom Cognito attributes)
// ---------------------------------------------------------------------------

/**
 * Read the cached preferred-barber value from the persisted session.
 * No network call — JWT was decoded at sign-in / refresh time.
 *
 * @returns {string | null} staffId UUID, or null if not set
 */
export function getPreferredBarber() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    return session?.preferredBarber || null;
  } catch (_) {
    return null;
  }
}

/**
 * Set the preferred barber on Cognito (custom:preferred_barber).
 * After Cognito acknowledges the write, refreshes the local session
 * so subsequent reads return the new value without a re-login.
 *
 * @param {string} staffId - UUID of staff member
 * @returns {Promise<string>} the staffId that was saved
 */
export function setPreferredBarber(staffId) {
  if (!staffId || typeof staffId !== 'string') {
    return Promise.reject({
      code: 'INVALID_PARAMETER',
      message: 'staffId is required.',
    });
  }
  return updateCustomAttribute('custom:preferred_barber', staffId)
    .then(() => {
      // Patch the in-memory session immediately. The JWT itself still
      // has the old value until the next refresh, but for UI purposes
      // we consider this the source of truth.
      patchSession({ preferredBarber: staffId });
      return staffId;
    });
}

/**
 * Clear the preferred-barber attribute. Cognito doesn't allow truly
 * deleting a custom attribute value, so we set it to an empty string,
 * which the SDK treats as "unset" on subsequent reads.
 */
export function clearPreferredBarber() {
  return updateCustomAttribute('custom:preferred_barber', '')
    .then(() => {
      patchSession({ preferredBarber: null });
    });
}

/**
 * Generic helper to update a single Cognito user attribute.
 * Requires a valid current session.
 */
function updateCustomAttribute(name, value) {
  return new Promise((resolve, reject) => {
    const cognitoUser = pool.getCurrentUser();
    if (!cognitoUser) {
      return reject({ code: 'UNAUTHORIZED', message: 'No active session.' });
    }
    // Must call getSession() before any authenticated action; this also
    // refreshes the JWT if needed.
    cognitoUser.getSession((sessErr, session) => {
      if (sessErr || !session?.isValid()) {
        return reject({ code: 'UNAUTHORIZED', message: 'Session expired.' });
      }
      const attr = new CognitoUserAttribute({ Name: name, Value: value });
      cognitoUser.updateAttributes([attr], (err) => {
        if (err) return reject(translateError(err));
        resolve();
      });
    });
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function persist(payload) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(payload)); } catch (_) {}
}

function patchSession(partial) {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return;
    const session = JSON.parse(raw);
    const merged = { ...session, ...partial };
    localStorage.setItem(SESSION_KEY, JSON.stringify(merged));
  } catch (_) {}
}

function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch (_) {}
}

function normalizePhone(raw) {
  // Cognito requires E.164: +14155552671
  const digits = raw.replace(/\D/g, '');
  if (raw.trim().startsWith('+')) return '+' + digits;
  if (digits.length === 10) return '+1' + digits;       // US default
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
  return '+' + digits;
}

/**
 * Maps Cognito error codes to a stable shape with friendly i18n keys.
 * UI screens decide whether to render the code or the raw message.
 */
function translateError(err) {
  const code = err?.code || err?.name || 'UnknownError';
  const message = err?.message || 'An unexpected error occurred.';
  return { code, message };
}

export const COGNITO_ERROR_CODES = Object.freeze({
  USERNAME_EXISTS:        'UsernameExistsException',
  CODE_MISMATCH:          'CodeMismatchException',
  EXPIRED_CODE:           'ExpiredCodeException',
  INVALID_PASSWORD:       'InvalidPasswordException',
  INVALID_PARAMETER:      'InvalidParameterException',
  USER_NOT_CONFIRMED:     'UserNotConfirmedException',
  USER_NOT_FOUND:         'UserNotFoundException',
  NOT_AUTHORIZED:         'NotAuthorizedException',
  LIMIT_EXCEEDED:         'LimitExceededException',
  TOO_MANY_REQUESTS:      'TooManyRequestsException',
  RESOURCE_NOT_FOUND:     'ResourceNotFoundException',
});
